import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import {
  createRuleSetFromPreset,
  fetchRuleSets,
  fetchRulesPresets,
  fetchRulesSettings,
  patchRulesSettings,
  updateRuleSet,
} from '../../features/rulesEngine/api';
import type { PresetId, RuleSet, RulesPreset, RuleSetting } from '../../features/rulesEngine/types';
import RulesSectionCard from './RulesSectionCard';

const PRESET_OPTIONS: Array<{ id: PresetId; label: string; hint: string }> = [
  { id: 'rta', label: 'RTA (UAE)', hint: 'Dubai / RTA-style daily limits' },
  { id: 'gb', label: 'GB domestic', hint: 'UK domestic working-time defaults' },
  { id: 'eu', label: 'EU / assimilated', hint: 'EU-style drivers’ hours defaults' },
  { id: 'manual', label: 'Manual', hint: 'Start blank; enable rules yourself' },
];

export default function RulesAdminPanel() {
  const theme = useTheme();
  const [presets, setPresets] = useState<RulesPreset[]>([]);
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RuleSet | null>(null);
  const [chooser, setChooser] = useState<PresetId>('rta');
  const [tagInput, setTagInput] = useState('');
  const [vocabulary, setVocabulary] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [p, rs, settings] = await Promise.all([
      fetchRulesPresets(),
      fetchRuleSets(),
      fetchRulesSettings(),
    ]);
    setPresets(p);
    setRuleSets(rs);
    setActiveId(settings.active_driver_rule_set_id ?? null);
    setVocabulary(settings.tag_vocabulary || []);
    const active = rs.find((r) => r.id === settings.active_driver_rule_set_id) || rs[0] || null;
    setDraft(active);
  }, []);

  useEffect(() => {
    void reload().catch((e) => {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setError(
          'Not authenticated against local HTTM. Sign out and sign in again (dev@example.com), then refresh.'
        );
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load');
      }
    });
  }, [reload]);

  const catalog = presets[0]?.catalog || [];
  const disclaimer = draft?.disclaimer || presets[0]?.disclaimer;
  const chooserMeta = PRESET_OPTIONS.find((p) => p.id === chooser) || PRESET_OPTIONS[0];

  const evaluatedCount = useMemo(
    () => catalog.filter((c) => c.evaluateInV1).length,
    [catalog]
  );
  const storedOnlyCount = catalog.length - evaluatedCount;

  const onCreateFromChooser = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const created = await createRuleSetFromPreset(chooser);
      await patchRulesSettings({ active_driver_rule_set_id: created.id });
      setMessage(`Created and activated “${created.name}”.`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const onSaveSettings = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await updateRuleSet(draft.id, {
        name: draft.name,
        settings: draft.settings,
        category: draft.category as 'operator_internal' | 'regulatory_external',
      });
      await patchRulesSettings({
        active_driver_rule_set_id: draft.id,
        tag_vocabulary: vocabulary,
      });
      setMessage('Rule set saved.');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const updateSetting = (key: string, patch: Partial<RuleSetting>) => {
    if (!draft) return;
    const current = draft.settings[key] || { enabled: false, value: null, severity: 'soft' as const };
    setDraft({
      ...draft,
      settings: {
        ...draft.settings,
        [key]: { ...current, ...patch },
      },
    });
  };

  return (
    <Box data-testid="rules.admin.panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Rule sets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose a legal template, tune daily limits, then activate the set used when validating duties
          and rostering proposals.
        </Typography>
      </Box>

      {disclaimer && (
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          data-testid="rules.admin.disclaimer"
        >
          {disclaimer}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          onClose={() => setMessage(null)}
        >
          {message}
        </Alert>
      )}

      <RulesSectionCard
        icon={<PlaylistAddCheckOutlinedIcon />}
        title="Create from preset"
        description="Start from a jurisdiction template. You can edit limits after creating."
      >
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 280px) 1fr auto' },
            alignItems: 'start',
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel id="rules-preset-label">Preset</InputLabel>
            <Select
              labelId="rules-preset-label"
              label="Preset"
              value={chooser}
              onChange={(e) => setChooser(e.target.value as PresetId)}
              data-testid="rules.admin.preset-select"
            >
              {PRESET_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ pt: { md: 1 } }}>
            {chooserMeta.hint}
          </Typography>
          <Button
            variant="contained"
            onClick={() => void onCreateFromChooser()}
            disabled={busy}
            startIcon={<GavelOutlinedIcon />}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
            data-testid="rules.admin.create-preset-btn"
          >
            Create rule set
          </Button>
        </Box>
      </RulesSectionCard>

      <RulesSectionCard
        icon={<TuneOutlinedIcon />}
        title="Active rule set"
        description="This set is applied to duty validation and Advance Rostering proposals."
        action={
          draft?.id === activeId ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<CheckCircleOutlineIcon />}
              label="Active"
            />
          ) : null
        }
      >
        {ruleSets.length === 0 ? (
          <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
            No rule sets yet. Create one from a preset above.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}
            >
              <FormControl size="small" fullWidth>
                <InputLabel id="rules-active-label">Rule set</InputLabel>
                <Select
                  labelId="rules-active-label"
                  label="Rule set"
                  value={draft?.id || ''}
                  onChange={(e) => {
                    const next = ruleSets.find((r) => r.id === e.target.value) || null;
                    setDraft(next);
                  }}
                  data-testid="rules.admin.active-select"
                >
                  {ruleSets.map((rs) => (
                    <MenuItem key={rs.id} value={rs.id}>
                      {rs.name} ({rs.source_preset})
                      {rs.id === activeId ? ' — active' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {draft && (
                <TextField
                  label="Display name"
                  size="small"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  data-testid="rules.admin.name-input"
                />
              )}
            </Box>

            {draft && (
              <>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<GavelOutlinedIcon />}
                    label={`Preset: ${draft.source_preset}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<TuneOutlinedIcon />}
                    label={`${evaluatedCount} evaluated in v1`}
                  />
                  {storedOnlyCount > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<InfoOutlinedIcon />}
                      label={`${storedOnlyCount} stored only`}
                    />
                  )}
                </Stack>

                <Divider />

                <Typography variant="subtitle2" fontWeight={600}>
                  Limits
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <LockOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                  Hard blocks saving. Soft warns and needs an override reason.
                </Typography>

                <Stack spacing={1.25}>
                  {catalog.map((item) => {
                    const setting =
                      draft.settings[item.key] ||
                      ({ enabled: false, value: null, severity: 'soft' } as RuleSetting);
                    return (
                      <Box
                        key={item.key}
                        data-testid={`rules.admin.setting.${item.key}`}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1.5,
                          px: 1.5,
                          py: 1.25,
                          bgcolor: setting.enabled
                            ? theme.palette.action.hover
                            : theme.palette.background.default,
                          opacity: setting.enabled ? 1 : 0.85,
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1.25}
                          alignItems={{ md: 'center' }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!setting.enabled}
                                onChange={(e) =>
                                  updateSetting(item.key, { enabled: e.target.checked })
                                }
                              />
                            }
                            label={
                              <Stack spacing={0.25}>
                                <Typography variant="body2" fontWeight={600}>
                                  {item.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.evaluateInV1
                                    ? `Evaluated · ${item.unit}`
                                    : `Stored only (not evaluated in v1) · ${item.unit}`}
                                </Typography>
                              </Stack>
                            }
                            sx={{ flex: 1, m: 0, alignItems: 'flex-start' }}
                          />
                          <TextField
                            size="small"
                            label="Value"
                            value={setting.value ?? ''}
                            disabled={!setting.enabled}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const asNum = Number(raw);
                              updateSetting(item.key, {
                                value:
                                  raw === ''
                                    ? null
                                    : Number.isFinite(asNum) && item.unit !== 'text'
                                      ? asNum
                                      : raw,
                              });
                            }}
                            sx={{ width: { xs: '100%', md: 120 } }}
                          />
                          <FormControl
                            size="small"
                            sx={{ minWidth: { xs: '100%', md: 140 } }}
                            disabled={!setting.enabled}
                          >
                            <InputLabel>Severity</InputLabel>
                            <Select
                              label="Severity"
                              value={setting.severity}
                              onChange={(e) =>
                                updateSetting(item.key, {
                                  severity: e.target.value as 'hard' | 'soft',
                                })
                              }
                            >
                              <MenuItem value="hard">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <LockOutlinedIcon fontSize="small" />
                                  <span>Hard</span>
                                </Stack>
                              </MenuItem>
                              <MenuItem value="soft">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <WarningAmberOutlinedIcon fontSize="small" color="warning" />
                                  <span>Soft</span>
                                </Stack>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={() => void onSaveSettings()}
                    disabled={busy}
                    startIcon={<SaveOutlinedIcon />}
                    data-testid="rules.admin.save-btn"
                  >
                    Save & activate
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </RulesSectionCard>

      <RulesSectionCard
        icon={<LocalOfferOutlinedIcon />}
        title="Vehicle tag vocabulary"
        description="Known capability tags used when matching routes to vehicles (e.g. accessible, coach)."
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label="Add tag"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            fullWidth
            data-testid="rules.admin.tag-input"
          />
          <Button
            variant="outlined"
            onClick={() => {
              const t = tagInput.trim();
              if (!t || vocabulary.includes(t)) return;
              setVocabulary([...vocabulary, t]);
              setTagInput('');
            }}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
            data-testid="rules.admin.tag-add-btn"
          >
            Add tag
          </Button>
        </Stack>
        <Box data-testid="rules.admin.tag-list">
          {vocabulary.length ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {vocabulary.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  onDelete={() => setVocabulary(vocabulary.filter((x) => x !== tag))}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No tags yet. Add ones your fleet uses, then assign them on the Vehicles tab.
            </Typography>
          )}
        </Box>
        <Tooltip title="Saving the active rule set also persists this vocabulary.">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Tip: click Save & activate on the rule set above to persist tag vocabulary.
          </Typography>
        </Tooltip>
      </RulesSectionCard>
    </Box>
  );
}
